/**
 * Auth utilities — JWT token management, login API, role checks.
 */
import axios from 'axios';

const API_BASE = '/api/v1/auth';

// ─── HTTP client with auto-attached token (for /api/v1/auth endpoints) ────
const authClient = axios.create({ baseURL: API_BASE });

authClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

authClient.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      const requestUrl = String(err.config?.url || '');
      const onLoginPage = window.location.pathname === '/login' || window.location.pathname.startsWith('/login');
      if (requestUrl.includes('/login') || onLoginPage) {
        return Promise.reject(err);
      }
      logout();
      window.location.href = '/login';
    }
    return Promise.reject(err);
  },
);

// ─── Global interceptors: attach JWT to ALL axios and fetch() calls ───────
// Axios: any axios.get/post/put/delete call gets JWT automatically
axios.interceptors.request.use((config) => {
  const token = getToken();
  if (token && !config.headers?.Authorization) {
    config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
  }
  return config;
});

// Fetch: wrap global fetch so all fetch() calls to /api/ get JWT
const _originalFetch = window.fetch;
window.fetch = function (url, options = {}) {
  const urlStr = typeof url === 'string' ? url : url?.url || '';
  if (urlStr.includes('/api/') || urlStr.startsWith('/api')) {
    const token = getToken();
    if (token) {
      options = {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${token}`,
        },
      };
    }
  }
  return _originalFetch.call(window, url, options);
};

// ─── Token helpers ─────────────────────────────────────────────────────────
export function getToken() {
  return localStorage.getItem('access_token');
}

export function getUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function isAuthenticated() {
  const token = getToken();
  if (!token) return false;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

export function logout() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('user');
  localStorage.removeItem('viewing_tenant_id');
  localStorage.removeItem('viewing_tenant_slug');
  localStorage.removeItem('viewing_tenant_name');
  try { sessionStorage.removeItem('mcc_pending_token'); } catch { /* ignore */ }
}

function mergeUserWithTokenClaims(access_token, user) {
  try {
    const parts = access_token.split('.');
    if (parts.length === 3) {
      const claims = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return {
        ...user,
        email: user.email || claims.email || '',
        role: user.role || claims.role || '',
        tenant_code: claims.tenant_code || user.tenant_code || '',
        tenant_id: claims.tenant_id ?? user.tenant_id ?? null,
        is_platform_user: Boolean(claims.is_platform_user ?? user.is_platform_user),
        is_poc: Boolean(claims.is_poc ?? user.is_poc),
        id: user.id || (claims.sub != null ? Number(claims.sub) || claims.sub : user.id),
      };
    }
  } catch {
    /* fall through */
  }
  return user;
}

export function persistSession(access_token, user) {
  localStorage.setItem('access_token', access_token);
  if (!user) return;
  const merged = mergeUserWithTokenClaims(access_token, user);
  localStorage.setItem('user', JSON.stringify(merged));
  // Do NOT clear viewing_tenant_id here — keep-alive / /me refresh must not
  // reset the platform switcher back to All Tenants / Core.
  window.dispatchEvent(new CustomEvent('pl-session-updated', { detail: merged }));
}

/** Call once on fresh login so superadmin lands on Paylegit Core (all data). */
export function resetPlatformViewToCore() {
  localStorage.removeItem('viewing_tenant_id');
}

/** Load profile from /me when a token exists but user cache is missing (e.g. SSO redirect). */
export async function hydrateUserSession() {
  const token = getToken();
  if (!token) return null;
  if (getUser()) return getUser();
  try {
    const res = await authAPI.me();
    const user = res.data?.user;
    if (user) {
      persistSession(token, user);
      return user;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function getUserRole() {
  const user = getUser();
  return user?.role || '';
}

export function isPlatformAdmin() {
  const user = getUser();
  return user?.is_platform_user && ['platform_admin', 'admin'].includes(user?.role);
}

export function isTenantAdmin() {
  return getUserRole() === 'tenant_admin';
}

export function isAnyAdmin() {
  return isPlatformAdmin() || isTenantAdmin();
}

/** Resolve whether the current workspace is Paylegit Demo. */
export function isDemoWorkspace(tenantsList) {
  const viewingId = localStorage.getItem('viewing_tenant_id');
  const user = getUser();
  const slug = String(user?.tenant_code || '').toLowerCase();
  if (!viewingId && (slug === 'paylegit-demo' || slug === 'paylegit_demo')) {
    return true;
  }
  if (!viewingId) return false;
  const list = tenantsList || [];
  const match = list.find((t) => String(t.id) === String(viewingId));
  const tslug = String(match?.slug || match?.tenant_code || '').toLowerCase();
  if (tslug === 'paylegit-demo' || tslug === 'paylegit_demo') return true;
  // Fallback: cached slug from last switch
  const cached = String(localStorage.getItem('viewing_tenant_slug') || '').toLowerCase();
  return cached === 'paylegit-demo' || cached === 'paylegit_demo';
}

export const demoAPI = {
  mapReports: (reportIds, targetTenantId = 'paylegit-demo') =>
    fetch('/api/demo/reports/map', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ report_ids: reportIds, target_tenant_id: targetTenantId }),
    }).then(async (r) => {
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Map failed');
      return data;
    }),
  hideReports: (reportIds, reason) =>
    fetch('/api/demo/reports/hide', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ report_ids: reportIds, reason }),
    }).then(async (r) => {
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Hide failed');
      return data;
    }),
  overlay: () =>
    fetch('/api/demo/reports/overlay', {
      headers: { ...getAuthHeaders() },
    }).then(async (r) => {
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Overlay failed');
      return data;
    }),
  scanCheck: (urls, extra = {}) =>
    fetch('/api/demo/scan-check', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ urls, ...extra }),
    }).then(async (r) => {
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const err = new Error(data.detail || 'Scan not allowed');
        err.status = r.status;
        err.data = data;
        throw err;
      }
      return data;
    }),
};

// ─── Auth API ──────────────────────────────────────────────────────────────
export const authAPI = {
  login: (email, password, extra = {}) => authClient.post('/login', { email, password, ...extra }, extra.device_id ? { headers: { 'X-PL-Device-Id': extra.device_id } } : undefined),
  deviceStatus: (pendingToken) => axios.get(`${API_BASE}/device-status`, {
    headers: { Authorization: `Bearer ${pendingToken}` },
  }),
  me: () => authClient.get('/me'),
  keepAlive: () => authClient.post('/keep-alive'),
  forgotPassword: (email) => authClient.post('/forgot-password', { email }),
  seed: () => authClient.post('/seed'),

  // Tenants
  listTenants: () => authClient.get('/tenants/'),
  createTenant: (data) => authClient.post('/tenants/', data),
  getTenant: (id) => authClient.get(`/tenants/${id}/`),
  updateTenant: (id, data) => authClient.put(`/tenants/${id}/`, data),
  deleteTenant: (id) => authClient.delete(`/tenants/${id}/`),

  // Users
  listUsers: (tenantId) => authClient.get('/users/' + (tenantId ? `?tenant_id=${tenantId}` : '')),
  createUser: (data) => authClient.post('/users/', data),
  getUser: (id) => authClient.get(`/users/${id}/`),
  updateUser: (id, data) => authClient.put(`/users/${id}/`, data),
  deleteUser: (id) => authClient.delete(`/users/${id}/`),

  // TOTP 2FA
  totpSetup: () => authClient.post('/totp/setup'),
  totpVerify: (code) => authClient.post('/totp/verify', { code }),
  totpDisable: (password) => authClient.post('/totp/disable', { password }),

  // Tools (name masking)
  listTools: () => authClient.get('/tools'),

  // Billing
  listBilling: (params) => authClient.get('/billing/' + (params ? `?${new URLSearchParams(params)}` : '')),
  createBilling: (data) => authClient.post('/billing/', data),
  billingSummary: (params) => authClient.get('/billing/summary' + (params ? `?${new URLSearchParams(params)}` : '')),
  billingSync: (data) => authClient.post('/billing/sync', data || {}),
  billingTenants: () => authClient.get('/billing/tenants'),

  // Merchant Risk History
  listRiskHistory: (params) => authClient.get('/risk-history/' + (params ? `?${new URLSearchParams(params)}` : '')),
  createRiskHistory: (data) => authClient.post('/risk-history/', data),
  updateRiskHistory: (id, data) => authClient.patch(`/risk-history/${id}/`, data),

  // API Keys
  listApiKeys: (tenantId) => authClient.get('/api-keys/' + (tenantId ? `?tenant_id=${tenantId}` : '')),
  createApiKey: (data) => authClient.post('/api-keys/', data),
  revokeApiKey: (id) => authClient.delete(`/api-keys/${id}/`),

  // Data Push Config
  listPushConfigs: (tenantId) => authClient.get('/data-push/' + (tenantId ? `?tenant_id=${tenantId}` : '')),
  createPushConfig: (data) => authClient.post('/data-push/', data),
  getPushConfig: (id) => authClient.get(`/data-push/${id}/`),
  updatePushConfig: (id, data) => authClient.put(`/data-push/${id}/`, data),
  deletePushConfig: (id) => authClient.delete(`/data-push/${id}/`),
  testPushConfig: (id) => authClient.post(`/data-push/${id}/test`),

  // Module Sync
  listModuleSync: (tenantId) => authClient.get('/module-sync/' + (tenantId ? `?tenant_id=${tenantId}` : '')),
  seedModuleSync: (tenantId) => authClient.post('/module-sync/seed', { tenant_id: tenantId }),
  configureModuleSync: (id, data) => authClient.put(`/module-sync/${id}/`, data),
  triggerModuleSync: (id) => authClient.post(`/module-sync/${id}/trigger`),
};

// ─── Add token to generic axios calls (for existing API calls) ─────────────
export function getAuthHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default authAPI;
